import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { CepStatus, CepStatusActions } from '../../../basics/enums/maya.enums';
import GridListAction from '../../GridListAction/GridListAction';

const useCepAdminDashboardListConfig = (
  setCepToFeedbackCallback: (cepId: string) => void,
  setCepToApprovedCallback: (cepId: string) => void,
  syncCepCallback: (cepId: string) => void,
  editCepCallback: (cepId: string) => void,
  duplicateCepCallback: (cepId: string) => void,
  openModalCallback: (cepId: string, action: CepStatusActions) => void,
) => {
  const { t } = useTranslation();
  const flags = useFlags();

  const cepTitleFieldConfig = {
    label: t('maya_cep_form_cep_name_label'),
    name: 'name',
    placeholder: t('maya_cep_form_cep_name_placeholder'),
    defaultValue: '',
    required: false,
  };

  const cepListColumnConfig: GridColDef[] = [
    {
      field: 'title',
      headerName: t('maya_cep_dashboard_title'),
      flex: 0.8,
      sortable: false,
      cellClassName: 'gridlist-cell-bold',
    },
    {
      field: 'action',
      headerName: '',
      disableColumnMenu: true,
      hideSortIcons: true,
      renderCell: (params: GridRenderCellParams) => {
        const cep = params.row;
        const actions = [];

        if (cep.status !== CepStatus.CANCELLED) {
          actions.push({
            label: t('actions_edit_label'),
            callback: () => { editCepCallback(cep._id); },
          });

          actions.push({
            label: t('actions_duplicate_label'),
            callback: () => { duplicateCepCallback(cep._id); },
          });
        }

        if (cep.status === CepStatus.DRAFT) {
          actions.push({
            label: t('maya_action_feedback_cep_label'),
            callback: () => {
              setCepToFeedbackCallback(cep._id);
            },
          });
        }

        if (cep.status === CepStatus.FEEDBACK) {
          actions.push({
            label: t('maya_action_approve_cep_label'),
            callback: () => {
              setCepToApprovedCallback(cep._id);
            },
          });
        }

        if (
          flags.mayaBiogenlinc3595EnableCepDeleting
          && (!flags.mayaBdcfcsd653ExtendEditingOfApprovedCep || !flags.mayaBdcfcsd1251EnableSettingCancelledStatusForCep)
          && cep.status !== CepStatus.APPROVED
        ) {
          actions.push({
            label: t('maya_action_delete_cep_label'),
            callback: () => {
              openModalCallback(cep._id, CepStatusActions.DELETE);
            },
          });
        }

        if (
          flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
          && flags.mayaBdcfcsd1251EnableSettingCancelledStatusForCep
          && cep.status !== CepStatus.APPROVED
          && cep.status !== CepStatus.CANCELLED
        ) {
          actions.push({
            label: t('maya_action_cancel_cep_label'),
            callback: () => {
              openModalCallback(cep._id, CepStatusActions.CANCEL);
            },
          });
        }

        if (
          flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
          && flags.mayaBdcfcsd1251EnableSettingCancelledStatusForCep
          && cep.status === CepStatus.CANCELLED
        ) {
          actions.push({
            label: t('maya_action_restore_cep_label'),
            callback: () => {
              openModalCallback(cep._id, CepStatusActions.RESTORE);
            },
          });
        }

        if (flags.mayaBiogenlinc2489VeevaSyncButtons) {
          actions.push({
            label: t('maya_action_sync_cep_label'),
            callback: () => {
              syncCepCallback(cep._id);
            },
          });
        }

        return (
          <GridListAction
            actions={ actions }
          />
        );
      },
      align: 'right',
      headerAlign: 'right',
      flex: 0.2,
    },
  ];

  return {
    cepListColumnConfig,
    cepTitleFieldConfig,
  };
};

export default useCepAdminDashboardListConfig;
